import {
  updateDocument,
  createDocument,
  getId,
  saveHasOneRelationship
} from 'services/firestore'
import { uploadImage } from 'helpers'
import { updateParentHasManyRelationship } from 'hooks'
import { Mockup } from 'models'
import { message } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useTranslations } from 'contexts/Translation'

const useActionsMockupAdvancedForm = ({ initialData, form } = {}) => {
  // [COMPONENT_STATE_HOOKS]
  const [loading, setLoading] = useState(false)

  // [ADDITIONAL_HOOKS]
  const { t } = useTranslations()
  const history = useHistory()
  const params = useParams()

  const prepareValues = async (values = {}, additionalValues = {}) => {
    const mockupId = initialData?.['mockup']?._id || getId('mockups')
    const additionalReferences = { mockupId }
    const preparedValues = {
      _id: mockupId,
      ...additionalValues,
      name: values?.['mockup']?.['name'] ?? null,
      imageUrl: (await uploadImage(values?.['mockup']?.['imageUrl'])) ?? null,
      description: values?.['mockup']?.['description'] ?? null,
      offerId: await saveHasOneRelationship(
        'offers',
        values['offerId'],
        additionalReferences
      )
    }
    if (params.customerId) preparedValues['customerId'] = params.customerId
    return preparedValues
  }

  const saveForm = async (values, callback) => {
    try {
      // Prepare data to be saved
      const data = await prepareValues(values)
      // Updating parent references
      if (params.customerId)
        await updateParentHasManyRelationship(
          params.customerId,
          'customers',
          'mockupIds',
          data._id
        )
      // Save data
      if (initialData) {
        await updateDocument('mockups', initialData.mockup?._id, data)
        message.success(t('Mockup successfully updated'))
      } else {
        await createDocument('mockups', data, data._id)
        message.success(t('Mockup successfully created'))
      }
      // Final callback
      callback?.()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Something went wrong during data save'))
    }
  }

  const validateForm = (values) => {
    try {
      // Prepare data to be validated
      const validationData = {
        name: values?.['mockup']?.['name'],
        imageUrl: values?.['mockup']?.['imageUrl'],
        description: values?.['mockup']?.['description']
      }
      Mockup.validationSchema.validateSync(validationData)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Mockup validation error: ') + t(error.message))
    }
  }

  const onFinish = async () => {
    if (loading) return // Avoid multiple calls

    try {
      setLoading(true)
      // Get form values
      const formValues = form.getFieldsValue()
      // Validate fields
      validateForm(formValues)
      // Final callback
      const callback = () => history.goBack()
      // Save data
      await saveForm(formValues, callback)
    } catch (error) {
      setLoading(false)
      message.error(error.message)
    }
  }

  const onReset = () => {
    form.resetFields()
    history.goBack()
  }

  return { onFinish, onReset, loading, saveForm, validateForm, prepareValues }
}

export default useActionsMockupAdvancedForm
